import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AsyncZipDownloadModal, AsyncZipPrepareResult, ZipPollOptions } from 'molo-shared';
import { totalDiscountAmount, roundAmount } from '../../../utils/priceHelper';
import Order from '../../../models/Order';
import { FaDownload } from 'react-icons/fa';
import FileApi, { mapFileApiZipResponse } from '../../../store/files/FileApi';

import { IGrouping } from './dashboard.d';

type PrepareZipFn = (
    onProgress: (message: string) => void,
    pollOptions?: ZipPollOptions
) => Promise<AsyncZipPrepareResult>;

interface IProps {
    productSortedByCategory: IGrouping;
    order: Order;
    categoryChanged?: Function;
    onClickFilterGroup?: Function;
    hidePrices?: boolean;
    showDownloadLink?: boolean;
}

const OrderSummary: React.FC<IProps> = (props) => {
    const [selectedCategory, setValue] = React.useState('categories');
    const { productSortedByCategory, order, categoryChanged, onClickFilterGroup, showDownloadLink } = props;
    const scrollableElement = React.createRef<HTMLDivElement>();
    const [hasOverflow, setHasOverflow] = useState<boolean>(false);
    const [zipDownloadShow, setZipDownloadShow] = useState(false);
    const [zipDownloadScopeLabel, setZipDownloadScopeLabel] = useState('');
    const zipDownloadPrepareRef = useRef<PrepareZipFn | null>(null);

    const prepareZip = useCallback<PrepareZipFn>(async (onProgress, pollOptions) => {
        const runPrepare = zipDownloadPrepareRef.current;
        if (!runPrepare) {
            throw new Error('Download could not be prepared.');
        }
        return runPrepare(onProgress, pollOptions);
    }, []);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    const handleResize = () => {
        if (scrollableElement.current) {
            if (+scrollableElement.current.scrollHeight > +scrollableElement.current.clientHeight) {
                setHasOverflow(true);
            } else if (+scrollableElement.current.scrollHeight === +scrollableElement.current.clientHeight) {
                setHasOverflow(false);
            }
        }
    };

    const onChangeSelect = (event: any) => {
        setValue(event.currentTarget.value);

        if (categoryChanged) {
            categoryChanged(event.currentTarget.value);
        }
    };

    const onClickHandler = (selectedCategoryValue: string, item: any) => {
        if (onClickFilterGroup) {
            onClickFilterGroup(selectedCategoryValue, item);
        }
    };

    const closeZipDownload = useCallback(() => {
        zipDownloadPrepareRef.current = null;
        setZipDownloadShow(false);
        setZipDownloadScopeLabel('');
    }, []);

    const onDownloadProductImages = () => {
        if (zipDownloadShow || !order || !order.orderLines || !order.orderLines[0]) {
            return;
        }

        const firstLine = order.orderLines[0];
        const scopeLabel = firstLine.bsl
            ? `BSL ${firstLine.bsl}`
            : `Order ${firstLine.navorderid}`;

        zipDownloadPrepareRef.current = async (onProgress, pollOptions) => {
            const response = firstLine.bsl
                ? await FileApi.createCustomerBSLItemsZip(firstLine.bsl, onProgress, pollOptions)
                : await FileApi.createCustomerUnshippedItemsZip(
                    firstLine.navorderid,
                    firstLine.deliveryCode,
                    onProgress,
                    pollOptions
                );
            return mapFileApiZipResponse(response);
        };

        setZipDownloadScopeLabel(scopeLabel);
        setZipDownloadShow(true);
    };

    const renderTableBody = () => {
        return Object.keys(productSortedByCategory[selectedCategory]).map((category, key) => {
            const item = productSortedByCategory[selectedCategory][category];

            return (
                <tr key={key} onClick={() => onClickHandler(selectedCategory, item)}>
                    <td>
                        <span style={{ textTransform: 'capitalize' }}>
                            {item.title.replaceAll('_', ' ')}
                        </span>
                    </td>
                    <td>{item.salesOptions}</td>
                    <td>{item.quantity}</td>
                    {!props.hidePrices && <td>{roundAmount(item.revenue)}</td>}
                </tr>
            );
        });
    };

    const getSalesOptionsTotal = () => {
        let total = 0;
        for (const i in productSortedByCategory[selectedCategory]) {
            total = total + productSortedByCategory[selectedCategory][i].salesOptions;
        }

        return total;
    };

    return (
        <div className="order-summary">
            <AsyncZipDownloadModal
                show={zipDownloadShow}
                onHide={closeZipDownload}
                scopeLabel={zipDownloadScopeLabel}
                prepareZip={prepareZip}
                onCancelJob={FileApi.cancelPackZipJob}
            />
            <select
                value={selectedCategory}
                onChange={onChangeSelect}
                style={{
                    textTransform: 'capitalize',
                    lineHeight: '1.5',
                    padding: '5px 10px',
                    minHeight: '36px',
                }}
            >
                {Object.keys(productSortedByCategory).length &&
                    Object.keys(productSortedByCategory).map((category, key) => {
                        return (
                            <option key={key} value={category}>
                                {category}
                            </option>
                        );
                    })}
            </select>
            <div className={'os-table__fixed' + (hasOverflow ? ' has-overflow' : ' regular')}>
                <table className="os-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Options</th>
                            <th>Items</th>
                            {!props.hidePrices && <th>Total</th>}
                        </tr>
                    </thead>
                </table>
            </div>
            <div className="os-table__scroller" ref={scrollableElement}>
                <table className="os-table">
                    <tbody>{Object.keys(productSortedByCategory).length ? renderTableBody() : null}</tbody>
                </table>
            </div>
            <div className={'os-table__fixed' + (hasOverflow ? ' has-overflow' : ' regular')}>
                <table className="os-table">
                    <tfoot>
                        {order && order.orderheader && order.orderheader.orderLinediscount ? (
                            <tr>
                                <td>
                                    Discount ( {order.orderheader.currencyCode}{' '}
                                    {order.orderheader.orderLinediscount}% )
                                </td>
                                <td></td>
                                <td></td>
                                <td>{roundAmount(totalDiscountAmount(order.orderLines))}</td>
                            </tr>
                        ) : null}
                        <tr>
                            <td className="bold">Grand total</td>
                            <td className="bold">{getSalesOptionsTotal()}</td>
                            <td className="bold">{order && order.orderTotals.noOfItems}</td>
                            {!props.hidePrices && (
                                <td className="bold">
                                    {order && order.orderheader.currencyCode}{' '}
                                    {order && roundAmount(order.orderTotals.amount)}
                                </td>
                            )}
                        </tr>
                        <tr>
                            {showDownloadLink &&
                            ((order &&
                                order.orderLines &&
                                order.orderLines[0] &&
                                order.orderLines[0].navorderid) ||
                                (order &&
                                    order.orderLines &&
                                    order.orderLines[0] &&
                                    order.orderLines[0].bsl)) ? (
                                <td colSpan={4} onClick={onDownloadProductImages}>
                                    <div
                                        style={{
                                            float: 'left',
                                            display: 'flex',
                                            backgroundColor: '#e9eef7',
                                            width: '100%',
                                            opacity: zipDownloadShow ? 0.6 : 1,
                                            cursor: zipDownloadShow ? 'wait' : 'pointer',
                                        }}
                                    >
                                        <span style={{ paddingTop: '3px' }}>
                                            <FaDownload />
                                        </span>
                                        <div style={{ marginLeft: '20px' }}>
                                            <a href="javascript:void(0)">Download images </a>
                                        </div>
                                    </div>
                                </td>
                            ) : (
                                <td colSpan={4} />
                            )}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default OrderSummary;
